import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FaStar, 
  FaChartLine, 
  FaUsers, 
  FaCheckCircle,
  FaComments,
  FaArrowUp as FaTrendUp,
  FaArrowDown as FaTrendDown,
  FaExclamationTriangle,
  FaSpinner,
  FaCalendarAlt,
  FaDownload
} from 'react-icons/fa';
import StarRating from './StarRating';
import { useRatings } from '../../hooks/useRatings';
import { RATING_CRITERIA_CONFIG } from '../../utils/ratingConstants';
import { formatRating, getRatingColorClass } from '../../utils/ratingHelpers';

/**
 * Comprehensive Rating Analytics Component for Restaurants
 * Shows detailed analytics, trends, and insights
 */
const RatingAnalytics = ({
  restaurantId,
  restaurantName = '',
  dateRange = '30d',
  className = ''
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState(dateRange);
  const [showInsights, setShowInsights] = useState(true);

  const { analytics, loading, error, refresh } = useRatings(restaurantId);

  // Refresh data when period changes
  useEffect(() => {
    if (restaurantId) {
      refresh();
    }
  }, [restaurantId, selectedPeriod, refresh]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <FaSpinner className="animate-spin text-orange-500 text-2xl mr-3" />
        <span className="text-gray-600">Loading analytics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <FaExclamationTriangle className="text-red-500 text-3xl mx-auto mb-3" />
        <h3 className="text-red-800 font-bold text-lg mb-2">Error Loading Analytics</h3>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={refresh}
          className="bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!analytics || !analytics.summary) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
        <FaChartLine className="text-gray-400 text-4xl mx-auto mb-4" />
        <h3 className="text-gray-700 font-bold text-lg mb-2">No Analytics Available</h3>
        <p className="text-gray-500">
          You need at least 5 ratings to see detailed analytics.
        </p>
      </div>
    );
  }

  const { summary, trends, engagement, insights } = analytics; // eslint-disable-line no-unused-vars

  // Calculate trend indicators
  const getTrendIndicator = (current, previous) => { // eslint-disable-line no-unused-vars
    if (!previous || previous === 0) return null;
    const change = ((current - previous) / previous) * 100;
    return {
      value: Math.abs(change).toFixed(1),
      isPositive: change > 0,
      isSignificant: Math.abs(change) > 5
    };
  };

  // Get performance insights
  const getPerformanceInsights = () => {
    const insights = [];
    const criteriaAverages = summary.criteriaAverages || {};
    
    // Find best and worst performing criteria
    const criteriaScores = Object.entries(criteriaAverages)
      .filter(([key]) => key !== 'overall')
      .sort(([,a], [,b]) => b - a);
    
    if (criteriaScores.length > 0) {
      const [bestCriteria, bestScore] = criteriaScores[0];
      const [worstCriteria, worstScore] = criteriaScores[criteriaScores.length - 1];
      
      if (bestScore >= 4.5) {
        insights.push({
          type: 'positive',
          icon: FaTrendUp,
          title: 'Excellent Performance',
          message: `Your ${RATING_CRITERIA_CONFIG[bestCriteria]?.label} is rated ${bestScore.toFixed(1)}/5 - customers love this!`
        });
      }
      
      if (worstScore < 3.5) {
        insights.push({
          type: 'warning',
          icon: FaTrendDown,
          title: 'Improvement Opportunity',
          message: `Consider focusing on ${RATING_CRITERIA_CONFIG[worstCriteria]?.label} (${worstScore.toFixed(1)}/5) to boost overall satisfaction.`
        });
      }
    }
    
    // Engagement insights
    if (engagement?.reviewRate < 30) {
      insights.push({
        type: 'info',
        icon: FaComments,
        title: 'Encourage Reviews',
        message: `Only ${engagement.reviewRate}% of customers leave written reviews. Consider following up to get more feedback.`
      });
    }
    
    if (engagement?.verifiedPurchaseRate > 80) {
      insights.push({
        type: 'positive',
        icon: FaCheckCircle,
        title: 'High Trust Score',
        message: `${engagement.verifiedPurchaseRate}% of your ratings are from verified purchases - this builds customer trust!`
      });
    }
    
    return insights;
  };

  const performanceInsights = getPerformanceInsights();

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Rating Analytics</h2>
          {restaurantName && (
            <p className="text-gray-600 mt-1">{restaurantName}</p>
          )}
        </div>
        
        <div className="flex items-center gap-3">
          {/* Period Selector */}
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-300 focus:border-orange-300"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 3 months</option>
            <option value="1y">Last year</option>
          </select>
          
          {/* Export Button */}
          <button
            onClick={() => {
              // Implement export functionality
              // console.log('Export analytics data');
            }}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <FaDownload />
            Export
          </button>
          
          {/* Refresh Button */}
          <button
            onClick={refresh}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            <FaCalendarAlt />
            Refresh
          </button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Overall Rating */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-6 border border-orange-100"
        >
          <div className="flex items-center justify-between mb-3">
            <FaStar className="text-orange-500 text-2xl" />
            <span className="text-sm text-gray-500">Overall</span>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-3xl font-bold ${getRatingColorClass(summary.averageRating)}`}>
              {formatRating(summary.averageRating)}
            </span>
            <StarRating value={summary.averageRating} readonly size="sm" />
          </div>
          <p className="text-sm text-gray-600 mt-2">
            Based on {summary.totalRatings} reviews
          </p>
        </motion.div>

        {/* Total Reviews */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm"
        >
          <div className="flex items-center justify-between mb-3">
            <FaUsers className="text-blue-500 text-2xl" />
            <span className="text-sm text-gray-500">Reviews</span>
          </div>
          <div className="text-3xl font-bold text-gray-800 mb-2">
            {summary.totalRatings || 0}
          </div>
          <p className="text-sm text-gray-600">
            Total customer reviews
          </p>
        </motion.div>

        {/* Verified Purchases */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm"
        >
          <div className="flex items-center justify-between mb-3">
            <FaCheckCircle className="text-green-500 text-2xl" />
            <span className="text-sm text-gray-500">Verified</span>
          </div>
          <div className="text-3xl font-bold text-green-600 mb-2">
            {engagement?.verifiedPurchaseRate || 0}%
          </div>
          <p className="text-sm text-gray-600">
            Verified purchases
          </p>
        </motion.div>

        {/* Review Rate */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm"
        >
          <div className="flex items-center justify-between mb-3">
            <FaComments className="text-purple-500 text-2xl" />
            <span className="text-sm text-gray-500">Reviews</span>
          </div>
          <div className="text-3xl font-bold text-purple-600 mb-2">
            {engagement?.reviewRate || 0}%
          </div>
          <p className="text-sm text-gray-600">
            With written reviews
          </p>
        </motion.div>
      </div>

      {/* Rating Breakdown */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm"
      >
        <h3 className="text-lg font-bold text-gray-800 mb-6">Rating Breakdown</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Criteria Breakdown */}
          <div>
            <h4 className="font-medium text-gray-700 mb-4">Performance by Criteria</h4>
            <div className="space-y-4">
              {Object.entries(RATING_CRITERIA_CONFIG).map(([key, config]) => {
                const score = summary.criteriaAverages?.[key] || 0;
                return (
                  <div key={key} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{config.icon}</span>
                      <span className="text-sm font-medium text-gray-700">
                        {config.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <StarRating value={score} readonly size="sm" />
                      <span className={`font-medium ${getRatingColorClass(score)}`}>
                        {formatRating(score)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Star Distribution */}
          <div>
            <h4 className="font-medium text-gray-700 mb-4">Star Distribution</h4>
            <div className="space-y-3">
              {[5, 4, 3, 2, 1].map((stars) => {
                const count = summary.ratingsBreakdown?.[stars] || 0;
                const percentage = summary.totalRatings > 0 
                  ? (count / summary.totalRatings) * 100 
                  : 0;
                
                return (
                  <div key={stars} className="flex items-center gap-3">
                    <div className="flex items-center gap-1 w-16">
                      <span className="text-sm font-medium">{stars}</span>
                      <FaStar className="text-yellow-400 text-xs" />
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-orange-400 to-red-400 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <div className="text-sm text-gray-600 w-12 text-right">
                      {count}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Performance Insights */}
      {performanceInsights.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-800">Performance Insights</h3>
            <button
              onClick={() => setShowInsights(!showInsights)}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              {showInsights ? 'Hide' : 'Show'} Insights
            </button>
          </div>
          
          {showInsights && (
            <div className="space-y-4">
              {performanceInsights.map((insight, index) => {
                const Icon = insight.icon;
                const colorClasses = {
                  positive: 'bg-green-50 border-green-200 text-green-800',
                  warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
                  info: 'bg-blue-50 border-blue-200 text-blue-800'
                };
                
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`p-4 rounded-lg border ${colorClasses[insight.type]}`}
                  >
                    <div className="flex items-start gap-3">
                      <Icon className="text-lg mt-0.5" />
                      <div>
                        <h4 className="font-medium mb-1">{insight.title}</h4>
                        <p className="text-sm opacity-90">{insight.message}</p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </motion.div>
      )}

      {/* Trends Chart Placeholder */}
      {trends?.daily && trends.daily.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm"
        >
          <h3 className="text-lg font-bold text-gray-800 mb-6">Rating Trends</h3>
          
          {/* Simple trend visualization - could be enhanced with Chart.js */}
          <div className="h-64 flex items-end justify-between gap-2 bg-gray-50 rounded-lg p-4">
            {trends.daily.slice(-7).map((day, index) => {
              const height = (day.averageRating / 5) * 100;
              return (
                <div key={index} className="flex flex-col items-center gap-2">
                  <div
                    className="bg-gradient-to-t from-orange-500 to-red-500 rounded-t min-w-8 transition-all duration-500"
                    style={{ height: `${height}%` }}
                  />
                  <div className="text-xs text-gray-600 text-center">
                    <div>{new Date(day.date).getDate()}</div>
                    <div className="font-medium">{day.averageRating.toFixed(1)}</div>
                  </div>
                </div>
              );
            })}
          </div>
          
          <p className="text-sm text-gray-600 mt-4 text-center">
            Daily average ratings for the last 7 days
          </p>
        </motion.div>
      )}
    </div>
  );
};

export default RatingAnalytics;